import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Star, Plus, Pause, Play, Trash2, Search } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import {
    listSponsored,
    createSponsored,
    pauseSponsored,
    resumeSponsored,
    deleteSponsored,
} from "@/lib/ads";
import { formatDate } from "@/lib/format";

export default function SponsoredListingsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (user && user.role !== "SUPER_ADMIN") {
            toast.error("Sponsored Listings requires Super Admin");
            navigate("/admin/ads");
        }
    }, [user, navigate]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ target_type: "", district_slug: "", status: "" });
    const [districts, setDistricts] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await listSponsored({ ...filters, limit: 200 });
            setRows(r);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
    }, [filters.target_type, filters.district_slug, filters.status]); // eslint-disable-line

    const onPause = async (r) => { try { await pauseSponsored(r.id); toast.success("Paused"); load(); } catch (e) { toast.error(errorMessage(e)); } };
    const onResume = async (r) => { try { await resumeSponsored(r.id); toast.success("Resumed"); load(); } catch (e) { toast.error(errorMessage(e)); } };
    const onDelete = async (r) => {
        if (!window.confirm("Delete sponsored listing?")) return;
        try { await deleteSponsored(r.id); toast.success("Deleted"); load(); } catch (e) { toast.error(errorMessage(e)); }
    };

    if (user && user.role !== "SUPER_ADMIN") return null;

    return (
        <div data-testid="sponsored-listings-page">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Monetization · Sponsored</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Sponsored Listings</h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Boost a post or a whole shop to the very top of the district feed.
                    </p>
                </div>
                <Button className="nest-gradient text-white" onClick={() => setCreateOpen(true)} data-testid="sponsored-create-btn">
                    <Plus size={14} className="mr-2" /> New Sponsored
                </Button>
            </div>

            <Card className="mb-4 rounded-2xl border border-neutral-200 p-3">
                <div className="flex flex-wrap gap-2">
                    <FilterSelect
                        placeholder="All target types"
                        value={filters.target_type}
                        onChange={(v) => setFilters((f) => ({ ...f, target_type: v }))}
                        items={[{ value: "POST", label: "Post" }, { value: "SHOP", label: "Shop" }]}
                        testid="sponsored-target-filter"
                    />
                    <FilterSelect
                        placeholder="All statuses"
                        value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        items={["SCHEDULED", "LIVE", "PAUSED", "EXPIRED"].map((s) => ({ value: s, label: s }))}
                        testid="sponsored-status-filter"
                    />
                    <FilterSelect
                        placeholder="All districts"
                        value={filters.district_slug}
                        onChange={(v) => setFilters((f) => ({ ...f, district_slug: v }))}
                        items={districts.map((d) => ({ value: d.slug, label: d.name }))}
                        testid="sponsored-district-filter"
                    />
                </div>
            </Card>

            <Card className="rounded-2xl border border-neutral-200">
                {loading ? (
                    <div className="space-y-2 p-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-12 rounded-xl" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center">
                        <Star size={32} className="mx-auto text-neutral-400" />
                        <div className="mt-2 text-sm font-semibold text-neutral-700">No sponsored listings</div>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Target</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>District</TableHead>
                                <TableHead>Priority</TableHead>
                                <TableHead>Window</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Impr.</TableHead>
                                <TableHead>Clicks</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`sponsored-row-${r.id}`}>
                                    <TableCell className="max-w-[220px] truncate font-mono text-[11px]">
                                        {r.target_id?.slice(0, 12)}…
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-[10px]">{r.target_type}</Badge>
                                    </TableCell>
                                    <TableCell className="text-xs capitalize">{r.district_slug}</TableCell>
                                    <TableCell className="text-xs">{r.priority}</TableCell>
                                    <TableCell className="text-xs">{formatDate(r.start_date)} → {formatDate(r.end_date)}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className={`text-[10px] uppercase ${r.status === "LIVE" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : r.status === "SCHEDULED" ? "border-blue-300 bg-blue-50 text-blue-700" : r.status === "PAUSED" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-neutral-300 bg-neutral-100 text-neutral-600"}`}>
                                            {r.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">{r.impressions_count}</TableCell>
                                    <TableCell className="text-xs">{r.clicks_count}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            {(r.status === "LIVE" || r.status === "SCHEDULED") && (
                                                <Button size="icon" variant="ghost" onClick={() => onPause(r)} title="Pause" data-testid={`sponsored-pause-${r.id}`}>
                                                    <Pause size={14} />
                                                </Button>
                                            )}
                                            {r.status === "PAUSED" && (
                                                <Button size="icon" variant="ghost" onClick={() => onResume(r)} title="Resume" data-testid={`sponsored-resume-${r.id}`}>
                                                    <Play size={14} />
                                                </Button>
                                            )}
                                            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(r)} title="Delete" data-testid={`sponsored-delete-${r.id}`}>
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <SponsoredCreateDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                districts={districts}
                onDone={() => { setCreateOpen(false); load(); }}
            />
        </div>
    );
}

function FilterSelect({ placeholder, value, onChange, items, testid }) {
    return (
        <Select value={value || "__any__"} onValueChange={(v) => onChange(v === "__any__" ? "" : v)}>
            <SelectTrigger className="w-44 text-xs" data-testid={testid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__any__">{placeholder}</SelectItem>
                {items.map((it) => (
                    <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function SponsoredCreateDialog({ open, onOpenChange, districts, onDone }) {
    const [form, setForm] = useState(() => defaults());
    const [searching, setSearching] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [busy, setBusy] = useState(false);

    function defaults() {
        const today = new Date().toISOString().slice(0, 10);
        const later = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);
        return {
            target_type: "POST",
            target_id: "",
            target_label: "",
            district_slug: "ernakulam",
            priority: 100,
            start_date: today,
            end_date: later,
            notes: "",
        };
    }

    useEffect(() => {
        if (open) {
            setForm(defaults());
            setCandidates([]);
        }
    }, [open]);

    const searchTargets = async (query) => {
        if (!query || query.length < 2) return setCandidates([]);
        setSearching(true);
        try {
            if (form.target_type === "POST") {
                const r = await api.get(`/posts?search=${encodeURIComponent(query)}&limit=10`);
                setCandidates(r.data.map((p) => ({ id: p.id, label: p.title, district: p.district_slug })));
            } else {
                const r = await api.get(`/admin/users?role=SHOP_OWNER`);
                setCandidates(r.data.filter((u) =>
                    (u.business_name || u.full_name || "").toLowerCase().includes(query.toLowerCase()),
                ).slice(0, 10).map((u) => ({ id: u.id, label: u.business_name || u.full_name, district: u.business_district })));
            }
        } finally {
            setSearching(false);
        }
    };

    const submit = async (force = false) => {
        if (!form.target_id) return toast.error("Please pick a target");
        try {
            setBusy(true);
            const payload = {
                target_type: form.target_type,
                target_id: form.target_id,
                district_slug: form.district_slug,
                priority: Number(form.priority) || 0,
                start_date: form.start_date,
                end_date: form.end_date,
                notes: form.notes || null,
            };
            await createSponsored(payload, force);
            toast.success("Sponsored listing created");
            onDone?.();
        } catch (e) {
            const s = e?.response?.status;
            if (s === 409) {
                if (window.confirm(`${e?.response?.data?.detail}\n\nSupersede the existing listing?`)) {
                    return submit(true);
                }
                return;
            }
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="sponsored-form-dialog">
                <DialogHeader>
                    <DialogTitle>New sponsored listing</DialogTitle>
                    <DialogDescription>Float a post or a shop to the very top of the district feed.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4">
                    <div>
                        <Label>Target type</Label>
                        <div className="mt-1 flex gap-2">
                            {["POST", "SHOP"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => { setForm({ ...form, target_type: t, target_id: "", target_label: "" }); setCandidates([]); }}
                                    className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${form.target_type === t ? "border-orange-500 bg-orange-50 text-orange-700" : "border-neutral-300 bg-white text-neutral-700"}`}
                                    data-testid={`sponsored-target-${t}`}
                                >
                                    {t === "POST" ? "Sponsor a Post" : "Sponsor a Shop"}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label>{form.target_type === "POST" ? "Search post" : "Search shop"}</Label>
                        <div className="relative">
                            <Input
                                placeholder={form.target_type === "POST" ? "Type an offer title" : "Type a business name"}
                                onChange={(e) => { searchTargets(e.target.value); }}
                                data-testid="sponsored-search-input"
                            />
                            <Search size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        </div>
                        {form.target_label && (
                            <div className="mt-1 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                                Selected: <b>{form.target_label}</b> ({form.target_id.slice(0, 8)}…)
                            </div>
                        )}
                        <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-neutral-200">
                            {searching && <div className="p-2 text-xs text-neutral-500">Searching…</div>}
                            {!searching && candidates.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => setForm({ ...form, target_id: c.id, target_label: c.label, district_slug: c.district || form.district_slug })}
                                    className="block w-full border-b border-neutral-100 p-2 text-left text-xs last:border-b-0 hover:bg-orange-50"
                                    data-testid={`sponsored-candidate-${c.id}`}
                                >
                                    <div className="font-semibold">{c.label}</div>
                                    <div className="text-neutral-500">{c.district || "—"}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>District *</Label>
                            <Select value={form.district_slug} onValueChange={(v) => setForm({ ...form, district_slug: v })}>
                                <SelectTrigger data-testid="sponsored-district-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {districts.map((d) => (
                                        <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Priority</Label>
                            <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="sponsored-priority-input" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Start date *</Label>
                            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sponsored-start-input" />
                        </div>
                        <div>
                            <Label>End date *</Label>
                            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sponsored-end-input" />
                        </div>
                    </div>

                    <div>
                        <Label>Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sponsored-notes-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={() => submit(false)} disabled={busy} className="nest-gradient text-white" data-testid="sponsored-submit-btn">
                        {busy ? "Creating…" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
